import { filter } from 'rxjs';
import { DOCUMENT, Location } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, NavigationStart, Router } from '@angular/router';
import { AppUpdateBrowser } from './app-update-browser.service';
import {
  buildReloadTarget,
  canAutoReload,
  CHECK_INTERVAL_MS,
  chunkReloadTarget,
  isChunkLoadError,
  isDifferentBuild,
  isPathChange,
  MIN_CHECK_GAP_MS,
  shouldReloadOnBoot
} from './app-update-rules';
import { readBuildInfo } from './build-info';

export type AppUpdateStatus = 'idle' | 'current' | 'available' | 'reloading';

export type UpdateCheckReason = 'boot' | 'resume' | 'interval';

/**
 * Új build észlelése. Induláskor, előtérbe kerüléskor és 5 percenként összeveti a futó build azonosítóját
 * a szerveren lévő version.json-nal. Eltérésnél induláskor azonnal újratölt, később sávot mutat, és a
 * következő útvonalváltást teljes betöltéssel végzi. A hiányzó oldalkódot (törölt chunk) is újratöltéssel kezeli.
 * Spec: docs/superpowers/specs/2026-09-16-app-update-detection-design.md (7.5)
 */
@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly browser = inject(AppUpdateBrowser);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly buildInfo = readBuildInfo(inject(DOCUMENT));
  private readonly state = signal<AppUpdateStatus>('idle');
  private readonly pollingStoppers: (() => void)[] = [];
  private startedAt = 0;
  private lastCheckAt = 0;
  private checking = false;
  private remoteId: string | null = null;

  readonly status = this.state.asReadonly();
  readonly updateAvailable = computed(() => this.state() === 'available' || this.state() === 'reloading');

  start(): void {
    if (!this.buildInfo || this.state() !== 'idle') {
      return;
    }
    this.state.set('current');
    this.startedAt = this.browser.now();
    this.forgetCompletedReload(this.buildInfo.id);
    this.watch();
    void this.check('boot');
  }

  async check(reason: UpdateCheckReason): Promise<void> {
    if (!this.buildInfo || this.checking || this.state() !== 'current') {
      return;
    }
    this.checking = true;
    this.lastCheckAt = this.browser.now();
    try {
      const remoteId = await this.browser.fetchRemoteBuildId();
      if (isDifferentBuild(this.buildInfo.id, remoteId)) {
        this.onDifferentBuild(remoteId, reason);
      }
    } finally {
      this.checking = false;
    }
  }

  /** A router hibakezelője hívja (withNavigationErrorHandler). Build-infó nélkül is működik. */
  handleNavigationError(error: NavigationError): void {
    if (!isChunkLoadError(error.error)) {
      return;
    }
    const target = chunkReloadTarget(error.url);
    if (this.canReload(target)) {
      this.reloadAutomatically(target, () => this.browser.assign(this.location.prepareExternalUrl(error.url)));
      return;
    }
    if (this.state() === 'current') {
      this.markAvailable();
    }
  }

  /** A sáv gombja: a user kifejezett kérése, ezért nincs hurokvédelem és kísérlet-feljegyzés. */
  reloadNow(): void {
    this.state.set('reloading');
    this.browser.reload();
  }

  private watch(): void {
    this.pollingStoppers.push(this.browser.onVisible(() => this.onResume()));
    this.pollingStoppers.push(this.zone.runOutsideAngular(() => this.browser.every(CHECK_INTERVAL_MS, () => this.onTick())));
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.onNavigationStart(event));
  }

  private onResume(): void {
    if (this.browser.now() - this.lastCheckAt >= MIN_CHECK_GAP_MS) {
      void this.check('resume');
    }
  }

  private onTick(): void {
    if (this.browser.isVisible()) {
      this.zone.run(() => void this.check('interval'));
    }
  }

  private onDifferentBuild(remoteId: string, reason: UpdateCheckReason): void {
    this.remoteId = remoteId;
    const target = buildReloadTarget(remoteId);
    const isEarlyBoot = reason === 'boot' && shouldReloadOnBoot(this.browser.now() - this.startedAt);
    if (isEarlyBoot && this.canReload(target)) {
      this.stopPolling();
      this.reloadAutomatically(target, () => this.browser.reload());
      return;
    }
    this.markAvailable();
  }

  private onNavigationStart(event: NavigationStart): void {
    if (this.state() !== 'available' || !this.remoteId || !isPathChange(this.router.url, event.url)) {
      return;
    }
    const target = buildReloadTarget(this.remoteId);
    if (!this.canReload(target)) {
      return;
    }
    const url = this.location.prepareExternalUrl(event.url);
    const replace = event.navigationTrigger === 'popstate' || !!this.router.getCurrentNavigation()?.extras.replaceUrl;
    this.reloadAutomatically(target, () => (replace ? this.browser.replace(url) : this.browser.assign(url)));
  }

  private markAvailable(): void {
    this.stopPolling();
    this.zone.run(() => this.state.set('available'));
  }

  private stopPolling(): void {
    this.pollingStoppers.splice(0).forEach((stop) => stop());
  }

  private canReload(target: string): boolean {
    return canAutoReload(this.browser.readAttempt(), target, this.browser.now());
  }

  private reloadAutomatically(target: string, navigate: () => void): void {
    this.browser.writeAttempt({ target, at: this.browser.now() });
    navigate();
  }

  private forgetCompletedReload(buildId: string): void {
    if (this.browser.readAttempt()?.target === buildReloadTarget(buildId)) {
      this.browser.clearAttempt();
    }
  }
}
